import { useEffect, useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Youtube } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";

const TABS = [
  { key: "paralimpiadi", testid: "podium-tab-paralimpiadi" },
  { key: "mondiali", testid: "podium-tab-mondiali" },
  { key: "coppa_mondo", testid: "podium-tab-coppa" },
  { key: "coppe", testid: "podium-tab-coppe" },
  { key: "collare", testid: "podium-tab-collare" },
];

const MEDAL = {
  gold: {
    place: "1", labelKey: "podio.place1", face: "podium-face-gold",
    heights: "h-52 sm:h-72 lg:h-80",
    badge: "bg-[radial-gradient(circle_at_32%_28%,#FDE68A,#F59E0B_55%,#92400E)]",
    glow: "0 0 44px rgba(245,158,11,0.5)",
    countTestId: "podium-gold-count",
  },
  silver: {
    place: "2", labelKey: "podio.place2", face: "podium-face-silver",
    heights: "h-36 sm:h-52 lg:h-60",
    badge: "bg-[radial-gradient(circle_at_32%_28%,#FFFFFF,#CBD5E1_55%,#64748B)]",
    glow: "0 0 34px rgba(203,213,225,0.4)",
    countTestId: "podium-silver-count",
  },
  bronze: {
    place: "3", labelKey: "podio.place3", face: "podium-face-bronze",
    heights: "h-28 sm:h-40 lg:h-48",
    badge: "bg-[radial-gradient(circle_at_32%_28%,#FDBA74,#D97706_55%,#7C2D12)]",
    glow: "0 0 30px rgba(217,119,6,0.45)",
    countTestId: "podium-bronze-count",
  },
};

function AnimatedNumber({ value, active }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!active) return;
    let start = null;
    let raf;
    const duration = Math.min(900 + value * 55, 2600);
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
      setDisplay(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    setDisplay(0);
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, active]);

  return <span>{display}</span>;
}

function PodiumColumn({ kind, count, delay, active }) {
  const { t } = useLang();
  const m = MEDAL[kind];
  return (
    <div className="flex flex-col items-center justify-end">
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={active ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
        transition={{ delay: delay + 0.45, type: "spring", stiffness: 260, damping: 16 }}
        className={`relative z-10 -mb-5 sm:-mb-7 flex h-14 w-14 sm:h-20 sm:w-20 items-center justify-center rounded-full border-2 border-white/25 ${m.badge}`}
        style={{ boxShadow: m.glow }}
      >
        <span className="font-display text-2xl sm:text-4xl text-[#090D14] drop-shadow-sm">{m.place}</span>
      </motion.div>
      <motion.div
        initial={{ scaleY: 0 }}
        animate={active ? { scaleY: 1 } : { scaleY: 0 }}
        transition={{ delay, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className={`relative w-24 sm:w-36 lg:w-44 ${m.heights} ${m.face} origin-bottom rounded-t-xl overflow-hidden`}
      >
        <span className="absolute inset-x-0 top-0 h-2.5 bg-white/40 blur-[1px]" />
        <span className="absolute inset-y-0 right-0 w-1/3 bg-black/20" />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            data-testid={m.countTestId}
            className="font-display text-5xl sm:text-7xl lg:text-8xl text-[#090D14] drop-shadow-[0_2px_2px_rgba(255,255,255,0.25)]"
          >
            <AnimatedNumber value={count} active={active} />
          </span>
          <span className="mt-1 text-[9px] sm:text-[11px] font-bold uppercase tracking-[0.25em] text-[#090D14]/80">
            {t(m.labelKey)}
          </span>
        </div>
      </motion.div>
    </div>
  );
}

/* Globo di cristallo stile trofeo Coppa del Mondo FIS, con piedistallo */
function CupTrophy({ count, labelKey, subKey, big, delay, active, testid }) {
  const { t } = useLang();
  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={active ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
      transition={{ delay, type: "spring", stiffness: 220, damping: 15 }}
      className="flex flex-col items-center"
      data-testid={testid}
    >
      <span className="flex flex-col items-center">
        <span
          className={`relative flex items-center justify-center rounded-full bg-[radial-gradient(circle_at_30%_25%,#FFFFFF,#BAE6FD_45%,#38BDF8_78%,#0369A1)] ${
            big ? "h-32 w-32 sm:h-44 sm:w-44 shadow-[0_0_60px_rgba(56,189,248,0.5)]" : "h-24 w-24 sm:h-32 sm:w-32 shadow-[0_0_40px_rgba(56,189,248,0.4)]"
          }`}
        >
          <span className="absolute inset-2.5 rounded-full border border-white/50" />
          <span
            data-testid={`${testid}-count`}
            className={`font-display text-[#090D14] drop-shadow-[0_2px_2px_rgba(255,255,255,0.4)] ${big ? "text-6xl sm:text-8xl" : "text-4xl sm:text-6xl"}`}
          >
            <AnimatedNumber value={count} active={active} />
          </span>
        </span>
        <span className={`h-2.5 bg-gradient-to-b from-slate-400 to-slate-600 ${big ? "w-10" : "w-8"}`} />
        <span className={`h-2 rounded-full border border-slate-600 bg-slate-700 ${big ? "w-20 sm:w-24" : "w-14 sm:w-16"}`} />
      </span>
      <span className="mt-4 text-center">
        <span className="font-display block text-base sm:text-xl text-slate-50 leading-tight">{t(labelKey)}</span>
        <span className="mt-1 block text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-sky-300/90">{t(subKey)}</span>
      </span>
    </motion.div>
  );
}

export default function Podio() {
  const { t } = useLang();
  const [counts, setCounts] = useState(null);
  const [tab, setTab] = useState("paralimpiadi");
  const podiumRef = useRef(null);
  const podiumInView = useInView(podiumRef, { margin: "-15% 0px" });

  useEffect(() => {
    api.get("/medal-counts").then((r) => setCounts(r.data)).catch(() => setCounts(null));
  }, []);

  const current = counts ? counts[tab] : { gold: 0, silver: 0, bronze: 0 };

  return (
    <section id="risultati" className="relative py-20 sm:py-32 overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(245,158,11,0.07),transparent_60%)]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <Reveal className="text-center max-w-2xl mx-auto">
          <span className="text-xs uppercase tracking-[0.3em] font-semibold text-sky-400">{t("podio.eyebrow")}</span>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl text-slate-50">
            {t("podio.titleA")}<span className="text-amber-400">{t("podio.titleAccent")}</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-slate-400">
            {t("podio.subtitle")}
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10 flex justify-center">
          <div className="grid w-full max-w-md grid-cols-2 gap-1 rounded-2xl border border-slate-800 bg-slate-900/60 p-1 backdrop-blur-md lg:w-auto lg:max-w-none lg:inline-flex lg:rounded-full">
            {TABS.map((tabDef) => (
              <button
                key={tabDef.key}
                data-testid={tabDef.testid}
                onClick={() => setTab(tabDef.key)}
                className={`w-full rounded-full px-3 py-2 sm:py-2.5 text-[9px] sm:text-xs font-bold uppercase tracking-[0.14em] sm:tracking-[0.18em] text-center transition-colors duration-300 lg:w-auto lg:px-5 ${
                  tabDef.key === "collare" ? "col-span-2" : ""
                } ${
                  tab === tabDef.key ? "bg-amber-400 text-[#090D14]" : "text-slate-400 hover:text-slate-100"
                }`}
              >
                {t(`podio.tabs.${tabDef.key}`)}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-14 sm:mt-20" data-testid="podium-container" ref={podiumRef}>
          <AnimatePresence mode="wait">
            {tab === "collare" ? (
              <motion.div
                key="collare"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, transition: { duration: 0.18 } }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col items-center"
                data-testid="collare-view"
              >
                <span className="relative flex flex-col items-center">
                  <span className="flex">
                    <span className="h-16 sm:h-20 w-10 sm:w-12 -skew-x-[16deg] rounded-t-sm bg-[linear-gradient(165deg,#16a34a,#f8fafc_52%,#dc2626)] shadow-lg" />
                    <span className="h-16 sm:h-20 w-10 sm:w-12 skew-x-[16deg] rounded-t-sm bg-[linear-gradient(195deg,#16a34a,#f8fafc_52%,#dc2626)] shadow-lg" />
                  </span>
                  <span className="relative -mt-2 flex h-44 w-44 sm:h-56 sm:w-56 items-center justify-center rounded-full bg-[radial-gradient(circle_at_32%_28%,#FDE68A,#F59E0B_55%,#92400E)] shadow-[0_0_70px_rgba(245,158,11,0.5)]">
                    <span className="absolute inset-3 rounded-full border-2 border-amber-100/60" />
                    <span className="absolute inset-5 rounded-full border border-amber-900/30" />
                    <span data-testid="collare-count" className="font-display text-7xl sm:text-8xl text-[#090D14] drop-shadow-[0_2px_2px_rgba(255,255,255,0.35)]"><AnimatedNumber value={6} active={podiumInView} /></span>
                  </span>
                </span>
                <span className="mt-7 text-center">
                  <span className="font-display block text-2xl sm:text-3xl text-slate-50 leading-tight">{t("podio.collareTitle")}</span>
                  <span className="mt-2 block text-[11px] uppercase tracking-[0.22em] text-amber-400/90">{t("podio.collareSub")}</span>
                  <span data-testid="collare-diploma-note" className="mt-3 inline-flex items-center gap-2 rounded-full border border-amber-400/50 bg-amber-400/10 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300">
                    {t("podio.diplomaNote")}
                  </span>
                </span>
              </motion.div>
            ) : tab === "coppe" ? (
              <motion.div
                key="coppe"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, transition: { duration: 0.18 } }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col items-center"
                data-testid="coppe-view"
              >
                <div className="flex flex-wrap items-start justify-center gap-12 sm:gap-24">
                  <CupTrophy big count={2} labelKey="podio.cupOverall" subKey="podio.cupOverallSub" delay={0.2} active={podiumInView} testid="cup-overall" />
                  <CupTrophy count={11} labelKey="podio.cupDiscipline" subKey="podio.cupDisciplineSub" delay={0.35} active={podiumInView} testid="cup-discipline" />
                </div>
              </motion.div>
            ) : (
              <motion.div
                key={tab}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.18 } }}
                className="flex flex-col"
              >
                <div className="flex items-end justify-center gap-2 sm:gap-5 lg:gap-8">
                  <PodiumColumn kind="silver" count={current.silver} delay={0.25} active={podiumInView} />
                  <PodiumColumn kind="gold" count={current.gold} delay={0.1} active={podiumInView} />
                  <PodiumColumn kind="bronze" count={current.bronze} delay={0.35} active={podiumInView} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <div className="mx-auto mt-[-2px] h-8 max-w-xl bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.65),transparent_70%)] blur-md" />
        </div>

        <Reveal delay={0.15} className="mt-12 flex flex-col items-center gap-3">
          <Link
            to="/risultati"
            data-testid="btn-view-all-results"
            className="group inline-flex items-center gap-3 rounded-full border border-amber-400/60 px-8 py-4 text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-amber-400 transition-all duration-300 hover:bg-amber-400 hover:text-[#090D14]"
          >
            {t("podio.ctaAll")}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
          <Link
            to="/video"
            data-testid="btn-race-videos"
            className="group inline-flex items-center gap-2 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-[#FF5252] transition-colors duration-300 hover:text-[#FF8080]"
          >
            <Youtube className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
            {t("podio.videoHint")}
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
